import posthog from "posthog-js";

// Shared with UserContext's opt-in-on-identify logic -- without a single
// source of truth here, a non-internal user identifying while the app
// happens to be running locally would get opt_in_capturing() called on
// them (that logic exists for the shared-machine case), silently undoing
// the opt-out below the moment anyone logs in during local dev.
export function isLocalEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  return (
    process.env.NODE_ENV === "development" ||
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  );
}

// One project across app.spidra.io and spidra.io (see spidra-landing-page's
// own lib/posthog.js) — both are subdomains of spidra.io, so PostHog's
// cross-domain cookie already treats a visitor as one identity across them,
// no bootstrapping needed.
export function initPostHog() {
  if (typeof window === "undefined") return;
  if (posthog.__loaded) return;

  const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
  if (!token) return; // analytics is additive — never block the app without it

  posthog.init(token, {
    // Routes through our own subdomain (see the managed reverse proxy set up
    // in PostHog → Settings → Managed reverse proxies) instead of talking to
    // posthog.com directly — ad-blockers that block posthog.com by name no
    // longer silently drop analytics. ui_host keeps the toolbar/session
    // recording deep-links pointing at the real PostHog UI, since the proxy
    // domain only serves the ingestion API, not the app itself.
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://relay.spidra.io",
    ui_host: "https://us.posthog.com",
    // "2026-05-30" defaults capture_pageview to "history_change", which fires
    // $pageview on every history.pushState/replaceState — exactly how Next's
    // router does client-side navigation, so no manual route-change capture
    // is needed on top of this (that would just double-count).
    defaults: "2026-05-30",
    person_profiles: "identified_only", // only create a profile once someone is identified (logged in)
    // /admin is never a customer-facing product surface -- drop every event
    // that fires while on it (pageviews, autocaptured clicks, anything
    // manually captured there later), regardless of who's viewing it. This
    // is a route-level rule, separate from the person-level team/test
    // account exclusion below (see UserContext's opt_out_capturing call) --
    // an admin could theoretically be a non-team account, and a team member
    // occasionally uses the regular dashboard too.
    before_send: cr => {
      if (window.location.pathname.startsWith("/admin")) return null;
      // Reset, verification and unsubscribe links carry a secret in the URL; it must never reach analytics.
      if (cr?.properties) {
        for (const [k, v] of Object.entries(cr.properties)) {
          if (typeof v === "string" && /[?&](token|t|code)=/.test(v)) cr.properties[k] = v.replace(/([?&](?:token|t|code)=)[^&#]*/g, "$1[redacted]");
        }
      }
      return cr;
    },
    loaded: ph => {
      if (process.env.NODE_ENV === "development") ph.debug();
      // PostHog's own multi-environment guide's documented pattern for this
      // exact problem: opt out inside `loaded` when running locally, so
      // `npm run dev` and anyone previewing a local build never sends real
      // events into the same shared project production uses.
      if (isLocalEnvironment()) {
        ph.opt_out_capturing();
        ph.set_config({ disable_session_recording: true });
      }
    },
  });
}

/**
 * For a click that immediately does a hard `window.location.href` redirect
 * (Stripe checkout, etc.) -- a plain posthog.capture() buffers and can be
 * dropped when the page unloads before it flushes. sendBeacon is built
 * specifically to survive that, so this doesn't need to be awaited before
 * redirecting.
 */
export function captureBeforeRedirect(event: string, properties: Record<string, unknown> | undefined, url: string): void {
  try {
    posthog.capture(event, properties, { transport: "sendBeacon" });
  } finally {
    window.location.href = url;
  }
}

export default posthog;
